import { useState, useEffect } from 'react';

interface ImagePreloadResult {
    loadedImages: Set<string>;
    isLoading: boolean;
    loadedCount: number;
    totalCount: number;
}

const isS3Url = (url: string): boolean => {
    return url.includes('amazonaws.com') || url.includes('s3.');
};

const isSameOrigin = (url: string): boolean => {
    try {
        const urlObj = new URL(url);
        return urlObj.origin === window.location.origin;
    } catch {
        return false;
    }
};

export const useImagePreloader = (imageUrls: string[]): ImagePreloadResult => {
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (imageUrls.length === 0) return;

        setIsLoading(true);
        const imagePromises: Promise<string>[] = [];

        imageUrls.forEach(url => {
            if (loadedImages.has(url)) return;

            if (isS3Url(url) && !isSameOrigin(url)) {
                setLoadedImages(prev => {
                    const newSet = new Set(prev);
                    newSet.add(url);
                    return newSet;
                });
                return;
            }

            const imagePromise = new Promise<string>((resolve, _) => {
                const img = new Image();

                img.onload = () => {
                    setLoadedImages(prev => {
                        const newSet = new Set(prev);
                        newSet.add(url);
                        return newSet;
                    });
                    resolve(url);
                };

                img.onerror = (error) => {
                    console.warn(`Failed to preload image: ${url}`, error);
                    setLoadedImages(prev => {
                        const newSet = new Set(prev);
                        newSet.add(url);
                        return newSet;
                    });
                    resolve(url);
                };

                if (!isSameOrigin(url) && !isS3Url(url)) {
                    img.crossOrigin = 'anonymous';
                }

                img.src = url;
            });

            imagePromises.push(imagePromise);
        });

        if (imagePromises.length > 0) {
            Promise.all(imagePromises).finally(() => {
                setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }
    }, [imageUrls, loadedImages]);

    return {
        loadedImages,
        isLoading,
        loadedCount: loadedImages.size,
        totalCount: imageUrls.length
    };
};
